<script lang="ts">
  import { onMount } from 'svelte'
  import { locale, tStore } from '../../i18n'
  import { savedFiltersApi, type SavedFilter } from '../../api/savedFilters'
  import type {
    ProcurementFilters,
    ProcurementStatus,
    ProcurementType,
  } from '../../api/procurements'
  import CpvCombobox from '../../components/CpvCombobox.svelte'
  import CountrySelect from '../../components/CountrySelect.svelte'
  import PageHeader from '../../components/PageHeader.svelte'
  import Dropdown from '../../components/Dropdown.svelte'
  import { useToast } from '../../composables/useToast'
  import { useAbortable } from '../../composables/useAbortable'
  import { formatDateTime } from '../../composables/format'
  import { toScore } from '../../composables/coerce'
  import { buildFilterSummary, filterSummaryText } from '../../composables/savedFilterSummary'
  import { loadCpvCodes } from '../../stores/cpvCodes'
  import type { CpvCode } from '../../api/cpvCodes'

  const toast = useToast()
  const { start } = useAbortable()

  let cpvCatalog = $state<CpvCode[]>([])

  let filters = $state<SavedFilter[]>([])
  let loading = $state(true)
  let errorMsg = $state<string | null>(null)

  let showForm = $state(false)
  let saving = $state(false)
  let form = $state({
    name: '',
    search: '',
    status: undefined as ProcurementStatus | undefined,
    procurementType: undefined as ProcurementType | undefined,
    country: undefined as string | undefined,
    // <input type="number"> binding reassigns this to a number or null at
    // runtime; the `string` annotation that was here lied and let `.trim()`
    // slip through into runtime.
    minAiScore: '' as number | '' | null,
    cpv: '',
  })

  onMount(() => {
    void load()
    // Suggestions-only dependency, same as CpvCombobox: the CPV summary
    // falls back to the bare code if this never resolves.
    void loadCpvCodes()
      .then((c) => {
        cpvCatalog = c
      })
      .catch(() => {})
  })

  async function load() {
    const signal = start()
    loading = true
    errorMsg = null
    try {
      filters = await savedFiltersApi.list({ signal })
    } catch (err) {
      if (signal.aborted) return // cancelled on teardown
      errorMsg = err instanceof Error ? err.message : String(err)
    } finally {
      if (!signal.aborted) loading = false
    }
  }

  async function submit(e: Event) {
    e.preventDefault()
    if (!form.name.trim()) return
    saving = true
    try {
      const config: ProcurementFilters = {}
      if (form.search.trim()) config.search = form.search.trim()
      if (form.status) config.status = form.status
      if (form.procurementType) config.procurementType = form.procurementType
      if (form.country) config.country = form.country
      const score = toScore(form.minAiScore)
      if (score !== undefined) config.minAiScore = score
      if (form.cpv.trim()) config.cpv = form.cpv.trim()

      const created = await savedFiltersApi.create({
        name: form.name.trim(),
        filterConfig: config,
      })
      filters = [...filters, created]
      resetForm()
      showForm = false
      toast.show($tStore('views.savedFilters.saved'), 'success')
    } catch (err) {
      toast.show(err instanceof Error ? err.message : String(err), 'error')
    } finally {
      saving = false
    }
  }

  async function remove(id: number) {
    try {
      await savedFiltersApi.remove(id)
      filters = filters.filter((f) => f.id !== id)
      toast.show($tStore('views.savedFilters.deleted'), 'success')
    } catch (err) {
      toast.show(err instanceof Error ? err.message : String(err), 'error')
    }
  }

  function resetForm() {
    form = {
      name: '',
      search: '',
      status: undefined,
      procurementType: undefined,
      country: undefined,
      minAiScore: '',
      cpv: '',
    }
  }

  // Awarded/cancelled/closed tenders are archived and never shown on the
  // Dashboard, so a saved filter using them could never match anything.
  const statusOptions: ProcurementStatus[] = ['published', 'in_review']
  const typeOptions: ProcurementType[] = ['simple', 'public']

  const statusDropdownOptions = $derived([
    { value: undefined, label: $tStore('common.all') },
    ...statusOptions.map((opt) => ({
      value: opt,
      label: $tStore(`views.procurements.status.${opt}`),
    })),
  ])
  const typeDropdownOptions = $derived([
    { value: undefined, label: $tStore('common.all') },
    ...typeOptions.map((opt) => ({ value: opt, label: $tStore(`views.procurements.type.${opt}`) })),
  ])
</script>

<section class="saved">
  <PageHeader
    title={$tStore('views.savedFilters.title')}
    subtitle={$tStore('views.savedFilters.subtitle')}
  >
    {#snippet actions()}
      <button class="btn btn--primary" onclick={() => (showForm = !showForm)}>
        {showForm ? $tStore('common.cancel') : $tStore('views.savedFilters.add')}
      </button>
    {/snippet}
  </PageHeader>

  {#if showForm}
    <form class="card form" onsubmit={submit}>
      <div class="form__row">
        <label class="field">
          <span class="field__label">{$tStore('views.savedFilters.form.name')}</span>
          <input class="input" type="text" bind:value={form.name} required />
        </label>
        <label class="field">
          <span class="field__label">{$tStore('views.savedFilters.form.search')}</span>
          <input class="input" type="text" bind:value={form.search} />
        </label>
      </div>

      <div class="form__row">
        <label class="field">
          <span class="field__label">{$tStore('views.savedFilters.form.status')}</span>
          <Dropdown bind:value={form.status} options={statusDropdownOptions} />
        </label>
        <label class="field">
          <span class="field__label">{$tStore('views.savedFilters.form.type')}</span>
          <Dropdown bind:value={form.procurementType} options={typeDropdownOptions} />
        </label>
      </div>

      <div class="form__row">
        <label class="field">
          <span class="field__label">{$tStore('views.procurements.filters.country')}</span>
          <CountrySelect bind:value={form.country} />
        </label>
        <label class="field">
          <span class="field__label">{$tStore('views.savedFilters.form.minScore')}</span>
          <input class="input" type="number" min="0" max="100" bind:value={form.minAiScore} />
        </label>
      </div>

      <div class="form__row">
        <label class="field">
          <span class="field__label">{$tStore('views.savedFilters.form.cpv')}</span>
          <CpvCombobox bind:value={form.cpv} placeholder="72" />
        </label>
      </div>

      <div class="form__actions">
        <button type="button" class="btn btn--secondary" onclick={() => (showForm = false)}>
          {$tStore('common.cancel')}
        </button>
        <button type="submit" class="btn btn--primary" disabled={saving}>
          {saving ? $tStore('common.saving') : $tStore('common.save')}
        </button>
      </div>
    </form>
  {/if}

  {#if loading}
    <p class="muted">{$tStore('views.savedFilters.loading')}</p>
  {:else if errorMsg}
    <p class="error">{errorMsg}</p>
  {:else if filters.length === 0}
    <p class="muted">{$tStore('views.savedFilters.empty')}</p>
  {:else}
    <div class="card table-wrap">
      <table class="table">
        <colgroup>
          <col class="col-name" />
          <col class="col-filter" />
          <col class="col-created" />
          <col class="col-actions" />
        </colgroup>
        <thead>
          <tr>
            <th>{$tStore('views.savedFilters.columns.name')}</th>
            <th>{$tStore('views.savedFilters.columns.filter')}</th>
            <th>{$tStore('views.savedFilters.columns.createdAt')}</th>
            <th class="actions">{$tStore('common.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {#each filters as f (f.id)}
            {@const summary = buildFilterSummary(f.filterConfig, $tStore, cpvCatalog, $locale)}
            <tr>
              <td>{f.name}</td>
              <td title={filterSummaryText(summary)}>
                {#if summary.length === 0}
                  —
                {:else}
                  <div class="filter-summary">
                    {#each summary as item (item.label)}
                      <span class="chip"
                        ><span class="chip__label">{item.label}:</span> {item.value}</span
                      >
                    {/each}
                  </div>
                {/if}
              </td>
              <td class="created-at">{formatDateTime(f.createdAt)}</td>
              <td class="actions">
                <button class="btn btn--sm btn--danger" onclick={() => remove(f.id)}>
                  {$tStore('common.delete')}
                </button>
              </td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>
  {/if}
</section>

<style lang="scss">
  @use '../../assets/styles/variables' as v;

  .saved {
    display: flex;
    flex-direction: column;
    gap: v.$space-lg;
  }

  .form {
    display: flex;
    flex-direction: column;
    gap: v.$space-md;
  }

  .form__row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: v.$space-md;

    @media (max-width: v.$bp-sm) {
      grid-template-columns: 1fr;
    }
  }

  .form__actions {
    display: flex;
    justify-content: flex-end;
    gap: v.$space-sm;
  }

  .table-wrap {
    // Below the table's min-width (see .table-wrap :global(.table)), the
    // table no longer fits the card — scroll the card horizontally instead
    // of squeezing columns down to unreadable widths, matching the same
    // convention as SourcesView's table-card.
    overflow-x: auto;
    padding: 0;
    // .saved's flex column gives every child a default min-width: auto,
    // which refuses to shrink below the table's min-width — so instead of
    // this card's own overflow-x scrolling, the whole card (and the page
    // around it) got pushed wider, producing a second, outer scrollbar.
    // min-width: 0 lets this card actually shrink and scroll internally.
    min-width: 0;
  }

  // table-layout: auto sizes each column by its widest *unbroken* content
  // (a chip never breaks mid-text), then hands any column without a hard
  // cap the table's leftover free width — so the Filter column, whose own
  // chips are individually short, still ballooned out far past what any
  // row's chips actually use, dragging Created at/Actions along with it and
  // leaving a wide gap between the header labels. Percentage widths on
  // every column (the same fixed-layout + colgroup pattern SourcesView
  // uses) make the table's own 100% width split proportionally instead, so
  // the columns stay evenly spaced regardless of viewport or chip count.
  .table-wrap :global(.table) {
    table-layout: fixed;
    width: 100%;
    // The floor below which columns stop being readable — chosen so
    // Created at's ~22% share still fits "29. 09. 2026. 10:22:02" on one
    // line. Above this the table fills the card exactly; below it, the
    // card scrolls horizontally instead of shrinking columns further.
    min-width: 56rem;
  }

  .col-name {
    width: 14%;
  }

  .col-filter {
    width: 50%;
  }

  .col-created {
    width: 22%;
  }

  .col-actions {
    width: 14%;
  }

  // Wrapped filter chips can make a row much taller than a single line of
  // text — vertical-align: middle (the table default) then leaves every
  // other cell floating in the middle of that height with a lopsided gap
  // above and below. Top-align every cell in this table instead, so a
  // short "name"/"created at" value sits flush with the first line of
  // chips no matter how tall the row grows.
  .table-wrap :global(td) {
    vertical-align: top;
  }

  // <col> only sizes the column — it can't carry text properties for the
  // cells inside it, so Created at's no-wrap has to live on the cell itself.
  .created-at {
    white-space: nowrap;
  }

  .actions {
    white-space: nowrap;
    text-align: right;
  }

  .filter-summary {
    display: flex;
    flex-wrap: wrap;
    gap: v.$space-xs;
  }

  .chip {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.1875rem 0.625rem;
    border-radius: v.$radius-pill;
    background: v.$color-surface;
    border: 1px solid v.$color-border;
    font-size: 0.8125rem;
    white-space: nowrap;
  }

  .chip__label {
    color: v.$color-text-muted;
  }
</style>
